import SelectComponent from "../../../../../../components/select/select-component.jsx";
import SentryRow from "../../../../../../components/template/sentry-row.jsx";
import FormField from "../../../../../../components/react-hook-form/form-field.jsx";
import {useFormContext} from "react-hook-form";
import {useMemo} from "react";
import {createBasicSetup, DEFAULT_TRIGGER_TYPE, DRIVERS, MODELS, MODELS_WITH_EXTRA_SETTINGS} from "../../../utils/lanes-form-utils.js";
import InputForm from "../../../../../../components/input/input-form.jsx";
import InputSwitch from "../../../../../../components/input/input-switch.jsx";

const OnePositionCameraForm = ({rule = ""}) => {
  const {setValue, watch, getValues} = useFormContext();
  const defaultRule = rule ? rule : watch("smartcaptureDriver.setup");
  const currentSetup = watch("smartcaptureDriver.setup");
  const driver = watch(`smartcaptureDriver.${defaultRule}.driver`);
  const model = watch(`smartcaptureDriver.${defaultRule}.model`);
  const mobile = watch("capturePlace.mobile");
  const disableConnection = watch(`smartcaptureDriver.${defaultRule}.disableConnection`)

  const DRIVERS_RULES = {
    back: {
      DRIVERS: mobile ? DRIVERS.filter(item => item.hasMobile && item.availableOn.includes("back")) : DRIVERS.filter(item => item.availableOn.includes("back"))
    },
    forward: {
      DRIVERS:  mobile ? DRIVERS.filter(item => item.hasMobile && item.availableOn.includes("forward")) : DRIVERS.filter(item => item.availableOn.includes("forward"))

    },
    forwardback: {
      DRIVERS: DRIVERS.filter(item => item.availableOn.includes("forwardback"))
    },
  }

  const MODEL_RULES = useMemo(() => {
    if(!driver) {
      return []
    }
    if(currentSetup === "forwardback"){
      return MODELS.filter(el => el.modelOf === driver && el.onlyForwardBack);
    }
    if(mobile){
      return MODELS.filter(el => el.isMobile && el.modelOf === driver && !el.onlyForwardBack);
    }
    return MODELS.filter(el => !el.isMobile && el.modelOf === driver && !el.onlyForwardBack)
  }, [driver, mobile, currentSetup]);

  const createSetupForDriver = (driverValue) => ({
    ...createBasicSetup(),
    driver: driverValue,
  });

  const applyModel = (modelValue) => {
    const base = "smartcaptureDriver." + defaultRule;
    const rule = MODEL_RULES.find(el => el.value === modelValue);

    setValue(base, {
      ...createBasicSetup(),
      driver,
      model: modelValue,
      disableConnection: rule?.defaultSmartValue ?? false,
      realTime: rule?.defaultRealTimeValue ?? false,
      port: rule?.defaultPort == null ? "" : String(rule.defaultPort)
    });

    if (!modelValue) return;

    if (MODELS_WITH_EXTRA_SETTINGS.includes(modelValue)) {
      setValue(base + ".smartLooptype", DEFAULT_TRIGGER_TYPE);
      setValue(base + ".model_details.saturacaoDia", 100);
      setValue(base + ".model_details.saturacaoNoite", 100);
      setValue(base + ".model_details.contrasteDia", 100);
      setValue(base + ".model_details.contrasteNoite", 100);
      setValue(base + ".model_details.numeroFotosDia", 1);
      setValue(base + ".model_details.numeroFotosNoite", 2);
      setValue(base + ".model_details.ganhoLuzVisivel", 32);
      setValue(base + ".model_details.ganhoLuzInfravermelha", 32);
      setValue(base + ".model_details.shutterMaximo", 23);
      setValue(base + ".model_details.porcentagemSegundoDisparo", 8);
      setValue(base + ".model_details.nivelDesejado", 27);
      setValue(base + ".model_details.smartSensibility", null);
      setValue(base + ".model_details.smartIrVirtual", 28);
      setValue(base + ".model_details.shutterFixo", 3);
      setValue(base + ".model_details.smartLevelNight", 12);
      setValue(base + ".model_details.autoIris", true);
      setValue(base + ".model_details.wrongWay", false);
    }
  };

  return(
    <SentryRow>
      {(defaultRule === "back" || defaultRule === "forward") &&
        <>
          <FormField
            name={`smartcaptureDriver.${defaultRule}.driver`}
            customRender={field => (
              <SelectComponent
                instanceId={`${defaultRule}.driverForm`}
                id={`${defaultRule}.driverForm`}
                label="Fabricante/Driver *"
                value={field.value}
                onChange={selected => {
                  const driverValue = selected?.value ?? "";

                  if(currentSetup === "forwardback") {
                    setValue("smartcaptureDriver.back", createSetupForDriver(driverValue), {shouldDirty: true});
                    setValue("smartcaptureDriver.forward", createSetupForDriver(driverValue), {shouldDirty: true});
                    setValue("forwardBackFirstSelected", driverValue ? rule : "", {shouldDirty: true});
                    return;
                  }

                  setValue("smartcaptureDriver." + defaultRule, createSetupForDriver(driverValue), {shouldDirty: true});
                }}
                options={DRIVERS_RULES[watch("smartcaptureDriver.setup")]?.DRIVERS ?? []}
                isDisabled={(currentSetup === "forwardback") && getValues("forwardBackFirstSelected") && getValues("forwardBackFirstSelected") !== rule}
                classNameWrapper="col-span-6"
              />
            )}
          />
          <FormField
            name={`smartcaptureDriver.${defaultRule}.model`}
            customRender={field => (
              <SelectComponent
                instanceId={`${defaultRule}.modelForm`}
                id={`${defaultRule}.modelForm`}
                label="Modelo *"
                value={field.value}
                onChange={(selected) => {
                  field.onChange(selected?.value ?? "");
                  applyModel(selected?.value ?? "");
                }}
                options={MODEL_RULES}
                classNameWrapper="col-span-6"
              />
            )}
          />
          { (model || currentSetup === "forwardback") &&
            <>
              <FormField
                name={`smartcaptureDriver.${defaultRule}.model_details.ipAddress`}
                as={InputForm}
                id={`${defaultRule}IpAddressForm`}
                label="Endpoint a ser configurado na câmera *"
                classNameWrapper="col-span-9"
                disabled={MODEL_RULES.find(el=> el.value === model)?.disabledEndPoint}
              />
              <FormField
                name={`smartcaptureDriver.${defaultRule}.model_details.portNo`}
                as={InputForm}
                id={`${defaultRule}PortNoForm`}
                label="Porta *"
                classNameWrapper="col-span-3"
                disabled={MODEL_RULES.find(el=> el.value === model)?.disabledEndPoint}
              />
              <FormField
                name={`smartcaptureDriver.${defaultRule}.disableConnection`}
                customRender={field => (
                  <InputSwitch
                    id={`${defaultRule}DisableConnectionForm`}
                    label="O smart terá acesso a câmera?"
                    checked={field.value}
                    onChange={() => field.onChange(!field.value)}
                    className="col-span-6"
                    disabled={MODEL_RULES.find(el=> el.value === model)?.disabledSmart}
                  />
                )}
              />
              <FormField
                name={`smartcaptureDriver.${defaultRule}.realTime`}
                customRender={field => (
                  <InputSwitch
                    id={`${defaultRule}RealTime`}
                    label="Monitorar em tempo real"
                    checked={field.value}
                    onChange={() => field.onChange(!field.value)}
                    className="col-span-6"
                    disabled={MODEL_RULES.find(el=> el.value === model)?.disabledRealTime || !disableConnection}
                  />
                )}
              />
              <FormField
                name={`smartcaptureDriver.${defaultRule}.cameraip`}
                as={InputForm}
                id={`${defaultRule}CameraIpForm`}
                label="Endereço da câmera *"
                classNameWrapper="col-span-9"
                disabled={MODEL_RULES.find(el=> el.value === model)?.disabledCameraIp || !disableConnection}
              />
              <FormField
                name={`smartcaptureDriver.${defaultRule}.port`}
                as={InputForm}
                id={`${defaultRule}PortForm`}
                label="Porta *"
                classNameWrapper="col-span-3"
                disabled={MODEL_RULES.find(el=> el.value === model)?.disabledPort || !disableConnection}
              />
              <FormField
                name={`smartcaptureDriver.${defaultRule}.model_details.user`}
                as={InputForm}
                id={`${defaultRule}UserForm`}
                label={`${MODEL_RULES.find(el=> el.value === model)?.requiredInput?.some(el => el.value === "model_details.user") ? "Usuário *" : "Usuário"}`}
                classNameWrapper="col-span-9"
                disabled={MODEL_RULES.find(el=> el.value === model)?.disabledUser || !disableConnection}
              />
              <FormField
                name={`smartcaptureDriver.${defaultRule}.model_details.password`}
                as={InputForm}
                id={`${defaultRule}PasswordForm`}
                label={`${MODEL_RULES.find(el=> el.value === model)?.requiredInput?.some(el => el.value === "model_details.password") ? "Senha *" : "Senha"}`}
                classNameWrapper="col-span-3"
                disabled={MODEL_RULES.find(el=> el.value === model)?.disabledPassword || !disableConnection}
              />
            </>
          }
        </>
      }
      {}
    </SentryRow>
  )
}

export default OnePositionCameraForm;